import { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import { VoucherSteps } from '../../constants'
import { isMultiItemArray, isValidArray } from 'mmfintech-commons'
import { PaymentMethods, useFetchSessionRequest, usePaymentRequest } from 'mmfintech-checkout-commons'

import type { VoucherOption, VoucherStep } from '../../@types'
import type { CountryChoice, PaymentOptionResponse } from 'mmfintech-checkout-commons/types'
import type { UseVoucherFlowOutput, UseVoucherFlowProps } from './useVoucherFlow.types'

export const useVoucherFlow = (props: UseVoucherFlowProps): UseVoucherFlowOutput => {
  const {
    handlePay,
    hasAdditionalFields,
    hasBankSelection,
    hasPreselectedBank,
    paymentOptions,
    paymentMethod,
    countryCode
  } = props

  const { checkoutCountries, checkoutPayError, session } = useSelector(
    ({ checkout: { checkoutCountries, checkoutPayError, session } }) => ({
      checkoutCountries,
      checkoutPayError,
      session
    }),
    shallowEqual
  )

  const [step, setStep] = useState<VoucherStep>(VoucherSteps.NONE)
  const [option, setOption] = useState<VoucherOption>('buy')
  const [triggered, setTriggered] = useState<boolean>(false)
  const [methodBlixcard, setMethodBlixcard] = useState<PaymentOptionResponse | null>(null)
  const [filteredMethods, setFilteredMethods] = useState<PaymentOptionResponse[]>([])

  const { resetError } = usePaymentRequest()
  const { setSessionError } = useFetchSessionRequest()

  const { country: sessionCountryCode, skipVoucherOptionSelection } = session || {}

  const hasOnlyBlixcard: boolean = useMemo(
    () => !isValidArray(filteredMethods) && Boolean(methodBlixcard),
    [filteredMethods, methodBlixcard]
  )
  const shouldSkipSelection: boolean = useMemo(
    () => !!skipVoucherOptionSelection || hasPreselectedBank,
    [skipVoucherOptionSelection, hasPreselectedBank]
  )
  const fieldsAfterBanks: boolean = useMemo(() => countryCode === 'KOR', [countryCode])

  const hasCountrySelected: boolean = useMemo(
    () =>
      Boolean(sessionCountryCode) &&
      checkoutCountries?.some((c: CountryChoice) => c.countryCode === sessionCountryCode),
    [sessionCountryCode, checkoutCountries]
  )

  const hasCountrySelection = useMemo(
    () => isMultiItemArray(checkoutCountries) && !hasCountrySelected,
    [checkoutCountries, hasCountrySelected]
  )

  const hasMethodSelection = useMemo(
    () => hasCountrySelection || isMultiItemArray(filteredMethods),
    [hasCountrySelection, filteredMethods]
  )

  const handleContinueWithPayment = () => {
    if (!triggered) {
      if (paymentMethod) {
        setTriggered(true)
        handlePay()
      } else {
        setSessionError('No payment methods available', 'CHECKOUT.PAYMENT.NO_METHODS')
      }
    }
  }

  const resetSessionError = useCallback(() => {
    setSessionError(null)
  }, [setSessionError])

  const hasBack = (currentStep: VoucherStep): boolean => {
    switch (currentStep) {
      case VoucherSteps.BANKS:
        return (fieldsAfterBanks ? hasMethodSelection : hasAdditionalFields) || !skipVoucherOptionSelection

      case VoucherSteps.FIELDS:
        return (fieldsAfterBanks ? hasBankSelection : hasMethodSelection) || !skipVoucherOptionSelection

      case VoucherSteps.METHODS:
      case VoucherSteps.REDEEM:
        return !skipVoucherOptionSelection

      default:
      // nothing
    }

    return false
  }

  const handleBack = () => {
    resetError()
    switch (step) {
      case VoucherSteps.METHODS:
      case VoucherSteps.REDEEM:
        setStep(VoucherSteps.OPTIONS)
        return

      case VoucherSteps.BANKS:
        if (!fieldsAfterBanks) {
          if (hasAdditionalFields) {
            setStep(VoucherSteps.FIELDS)
            return
          }
          if (hasMethodSelection) {
            setStep(VoucherSteps.METHODS)
            return
          }
        }
        setStep(VoucherSteps.OPTIONS)
        return

      case VoucherSteps.FIELDS:
        if (fieldsAfterBanks && hasBankSelection) {
          setStep(VoucherSteps.BANKS)
          return
        }
        if (hasMethodSelection) {
          setStep(VoucherSteps.METHODS)
          return
        }
        setStep(VoucherSteps.OPTIONS)
        return
    }
  }

  const assignNextStep = (currentStep: VoucherStep): void => {
    if (currentStep === VoucherSteps.NONE && !shouldSkipSelection) {
      setStep(VoucherSteps.OPTIONS)
      return
    }

    if (step === VoucherSteps.OPTIONS && option === 'redeem') {
      setStep(VoucherSteps.REDEEM)
      return
    }

    if (currentStep === VoucherSteps.NONE || currentStep === VoucherSteps.OPTIONS) {
      if (hasMethodSelection) {
        setStep(VoucherSteps.METHODS)
        return
      }
    }

    if (
      currentStep === VoucherSteps.NONE ||
      currentStep === VoucherSteps.OPTIONS ||
      currentStep === VoucherSteps.METHODS
    ) {
      if (fieldsAfterBanks) {
        if (hasBankSelection) {
          setStep(VoucherSteps.BANKS)
          return
        }
      } else {
        if (hasAdditionalFields) {
          setStep(VoucherSteps.FIELDS)
          return
        }
      }
    }

    if (fieldsAfterBanks) {
      if (hasAdditionalFields && currentStep !== VoucherSteps.FIELDS) {
        setStep(VoucherSteps.FIELDS)
        return
      }
    } else {
      if (hasBankSelection && currentStep !== VoucherSteps.BANKS) {
        setStep(VoucherSteps.BANKS)
        return
      }
    }

    if (session) {
      handleContinueWithPayment()
    }
  }

  const initialize = () => {
    step === VoucherSteps.NONE && assignNextStep(step)
  }

  const reset = () => {
    setStep(VoucherSteps.NONE)
    setOption('buy')
    setTriggered(false)
  }

  const resetMethods = () => {
    setMethodBlixcard(null)
    setFilteredMethods([])
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: initialize on mount
  useEffect(() => {
    initialize()
  }, [])

  useEffect(() => {
    if (isValidArray(paymentOptions)) {
      const blixcard = paymentOptions.find(option => option.paymentMethod === PaymentMethods.BLIXCARD)
      setMethodBlixcard(blixcard ?? null)
      const methods = paymentOptions.filter(option => option.paymentMethod !== PaymentMethods.BLIXCARD)
      setFilteredMethods(methods)

      if (!isValidArray(methods) && blixcard) {
        setOption('redeem')
        setStep(VoucherSteps.REDEEM)
      }
    } else {
      setMethodBlixcard(null)
      setFilteredMethods([])
    }
  }, [paymentOptions])

  // biome-ignore lint/correctness/useExhaustiveDependencies: execute only if checkoutPayError occur
  useEffect(() => {
    if (checkoutPayError) {
      if (triggered) {
        setTriggered(false)
      }
      initialize()
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    }
  }, [checkoutPayError])

  return {
    step,
    setStep,
    option,
    setOption,
    triggered,
    methodBlixcard,
    filteredMethods,
    hasBack,
    hasCountrySelected,
    hasOnlyBlixcard,
    handleBack,
    handleNext: () => assignNextStep(step),
    reset,
    resetError: resetSessionError,
    resetMethods,
    continueWithPayment: handleContinueWithPayment
  }
}
